import { useEffect, useState } from "react";

import { chatApiInstance } from "../apis/chatApi";
import type { AutomationCapacity } from "../types/integration";

/**
 * The capacities the Auto Mode badge shows; plain chat (and anything unknown) gets none.
 *
 * Auto Resolver is the only automated capacity. Reserved legacy values read as plain chat and
 * show no badge.
 */
const AUTOMATED_CAPACITIES: ReadonlyArray<AutomationCapacity> = [3];

/** The thread's mode as a badge capacity, or null for plain chat or an unreadable answer. */
export function automatedCapacity(raw: unknown): AutomationCapacity | null {
  const n = Number(raw);
  return AUTOMATED_CAPACITIES.includes(n as AutomationCapacity) ? (n as AutomationCapacity) : null;
}

/**
 * The message the thread's mode is shown on: its first user message, unless that message already
 * carries a mode of its own (it was sent in this session, with one).
 */
export function threadAutomationBadgeMessageId<M extends { message_id?: string; automation_capacity?: AutomationCapacity }>(
  messages: M[],
  isAssistant: (message: M) => boolean
): string | null {
  const first = messages.find((message) => !isAssistant(message));
  if (!first || first.automation_capacity) return null;
  return first.message_id ?? null;
}

/**
 * The thread's Auto Mode, read once its messages have loaded, or null.
 *
 * A stored message does not say which mode it ran in, so after a reload -- or on a thread the
 * poller started -- nothing would show the badge. The thread's current mode is the next best
 * answer, and it is shown on the message that started the run. Cosmetic: any failure is no badge.
 */
const useThreadAutomationBadge = (
  aiThreadId: string | number | null | undefined,
  hasMessages: boolean,
  liveCapacity?: 0 | 3
): AutomationCapacity | null => {
  const threadKey = aiThreadId != null && aiThreadId !== "" ? String(aiThreadId) : null;
  const [loaded, setLoaded] = useState<{ threadKey: string; capacity: AutomationCapacity | null } | null>(null);
  const loadedKey = loaded?.threadKey ?? null;

  useEffect(() => {
    if (!threadKey || !hasMessages || loadedKey === threadKey || liveCapacity !== undefined) return;
    let cancelled = false;
    chatApiInstance
      .getThreadAutomation(threadKey)
      .then((response) => {
        if (!cancelled) setLoaded({ threadKey, capacity: automatedCapacity(response?.automation_capacity) });
      })
      .catch(() => {
        if (!cancelled) setLoaded({ threadKey, capacity: null });
      });
    return () => {
      cancelled = true;
    };
  }, [threadKey, hasMessages, loadedKey, liveCapacity]);

  if (liveCapacity !== undefined) return automatedCapacity(liveCapacity);
  return loaded && loaded.threadKey === threadKey ? loaded.capacity : null;
};

export default useThreadAutomationBadge;
